<template>
  <form class="dashboard-block">
    <h2>{{ $t('management') | sentenceCase }}</h2>

    <div class="panel panel-grey">
      <div class="panel-body">
        <div class="row row-clear-md-4">
          <div class="col-xs-12 col-md-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">{{ $t('management') | sentenceCase}}</label>

              <selectize
                class="form-control selectize selectize-single"
                name="management"
                :disabled="$userIsManager"
                v-model="stateManagement"
                :options="managementOptions"
                :score="managementScoringCallback"
                :fetch-results="fetchManagementCallback"
                :fetch-more-results="fetchMoreMoreManagementCallback"/>
            </div>
          </div>

          <div class="col-xs-12 col-md-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">{{ $t('Booking Agent') }}</label>

              <selectize
                class="form-control selectize selectize-single"
                name="manager"
                v-model="stateStaffManager"
                :options="staffManagerOptions"/>
            </div>
          </div>

          <div class="col-xs-12 col-md-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">Notification Delivery</label>

              <selectize
                class="form-control selectize selectize-single"
                name="notification_preference"
                :disable-delete="true"
                :disabled="haveNoManagement"
                v-model="stateNotificationPreference"
                :options="notificationPreferenceOptions"/>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-footer">
        <ladda-button
          type="submit"
          class="btn btn-sm btn-primary"
          :click="handleSaveChangesCallback">
          Save Changes
        </ladda-button>
      </div>
    </div>
  </form>
</template>

<script>
import { stateMapper } from 'mixins/users'
import { mapActions, mapState } from 'vuex'
import { concat, map } from 'lodash'
import { notify } from 'utils'
import FlatPickr from 'vue-flatpickr-component'
import Selectize from '../ext/codium/CodiumSelectize'

export default {
  mixins: [stateMapper],

  data () {
    return {
      flatPickrConfig: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d 00:00:00',
        wrap: true
      }
    }
  },

  components: {
    FlatPickr,
    Selectize
  },

  created () {
    new Promise((resolve) => {
      if (this.$userIsAdmin) {
        const params = {
          sort: 'company',
          order: 'asc'
        }
        return this.getManagers(params)
          .then(resolve)
      }

      return resolve()
    }).then(() => {
      if (this.user.management_id) {
        return this.getManagerStaffUsers({ id: this.user.management_id, params: { status: 'active' } })
      }
    })
  },

  watch: {
    stateManagement (newValue, oldValue) {
      this.stateStaffManager = null

      if (this.user.management_id === null) {
        this.stateNotificationPreference = 2

        this.resetManagerStaffUsers()
      } else {
        if (this.user.profile.notification_preference === 2 || oldValue === null || oldValue === 'none') {
          this.stateNotificationPreference = 1
        }

        this.getManagerStaffUsers({ id: this.user.management_id, params: { status: 'active' } })
      }
    }
  },

  computed: {
    ...mapState('manager', ['managers']),
    ...mapState('manager/staff', ['staffUsers']),

    haveNoManagement () {
      return this.stateManagement === 'none'
    },

    managementOptions () {
      if (this.user.management) {
        return [
          { id: 'none', text: 'No ' + this.$t('management') },
          { id: this.user.management.id, text: this.user.management.company },
          ...map(this.managers, manager => ({ id: manager.id, text: manager.company }))
        ]
      } else {
        return [
          { id: 'none', text: 'No ' + this.$t('management') },
          ...map(this.managers, manager => ({ id: manager.id, text: manager.company }))
        ]
      }
    },

    staffManagerOptions () {
      return concat(
        [{ id: 'none', text: 'No ' + this.$t('manager') }],
        map(this.staffUsers, manager => ({ id: manager.id, text: manager.name }))
      )
    },

    notificationPreferenceOptions () {
      return [
        {
          id: 0,
          text: 'Talent + ' + this.$t('manager') + ' receive notifications'
        },
        {
          id: 1,
          text: 'Only ' + this.$t('manager') + ' receives notifications'
        },
        {
          id: 2,
          text: 'Only talent receives notifications'
        }
      ]
    }
  },

  methods: {
    ...mapActions('user', ['updateUser']),
    ...mapActions('manager', ['addManagers', 'getManagers', 'getManagersForNextPage']),
    ...mapActions('manager/staff', ['getManagerStaffUsers', 'resetManagerStaffUsers']),

    handleSaveChangesCallback () {
      return this.validate()
        .then(() => {
          return this.updateUser({
            id: this.user.id,
            payload: {
              management_id: this.user.management_id,
              manager_id: this.user.manager_id,
              commission_rate: this.user.commission_rate,
              profile: {
                notification_preference: this.user.profile.notification_preference
              }
            }
          })
        })
        .then(() => {
          notify.success('Successfully saved talent management details.')
        })
        .catch((error) => {
          if (error.response && error.response.status === 422) {
            notify.error('There was a problem updating talent management details. Please try again.')
          } else {
            throw error
          }
        })
    },

    fetchManagementCallback (company, callback) {
      const params = {
        keywords: company,
        sort: 'company',
        order: 'asc'
      }
      return this.getManagers(params)
        .then(() => {
          callback(this.managementOptions)
        })
    },

    fetchMoreMoreManagementCallback (company, callback) {
      return this.getManagersForNextPage({ company, sort: 'company', order: 'asc' })
        .then(() => {
          callback(this.managementOptions)
        })
        .catch(() => {})
    },

    managementScoringCallback (option, selectizeScore) {
      if (option.id === 'none') {
        return 1
      }

      return selectizeScore(option)
    }
  }
}
</script>
